import AccountIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import {
	AppBar,
	Box,
	Button,
	Divider,
	Grid,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	Toolbar,
	Typography,
	colors,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import useUserContext from "../../contexts/UserContext";
import { useOnBehalfOfValue } from "../../recoil/admin/hooks";
import { useState } from "react";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import LogoutIcon from "@mui/icons-material/Logout";

interface HeaderProps {
	onDrawerToggle: () => void;
}

export default function DashboardHeader({ onDrawerToggle }: HeaderProps) {
	const behalfOf = useOnBehalfOfValue();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { activeAccount } = useUserContext();
	const activeAccountName = !activeAccount ? t("account.noActiveAccount") : activeAccount.accountName;

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const isMenuOpen = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	return (
		<>
			{behalfOf && (
				<Box
					sx={{
						width: "100vw",
						backgroundColor: "yellow",
						display: "flex",
						alignItems: "center",
					}}
				>
					<Typography variant="h6" color="black" sx={{ textAlign: "center" }}>
						You're currently acting on behalf of {behalfOf.firstname} {behalfOf.lastname} (
						{behalfOf.phoneNumber})
					</Typography>
				</Box>
			)}
			<AppBar color="primary" position="sticky" elevation={0}>
				<Toolbar>
					<Grid container spacing={1} alignItems="center" color="white">
						<Grid sx={{ display: { sm: "none", xs: "block" } }} item>
							<IconButton color="inherit" aria-label="open drawer" onClick={onDrawerToggle} edge="start">
								<MenuIcon />
							</IconButton>
						</Grid>
						<Grid item xs />

						<Grid item>
							{activeAccount && (
								<Button
									variant="text"
									onClick={handleClick}
									sx={{
										maxWidth: { xs: theme.spacing(24), md: "100%" },
										backgroundColor: isMenuOpen ? theme.palette.primary.dark : "none",
										color: theme.palette.common.white,
									}}
								>
									<Typography
										variant="body1"
										sx={{
											textOverflow: "ellipsis",
											overflow: "hidden",
											whiteSpace: "nowrap",
										}}
									>
										{activeAccountName}
									</Typography>
									<AccountIcon sx={{ ml: 1.5 }} />
								</Button>
							)}
							{!activeAccount && (
								<IconButton
									color="inherit"
									sx={{ p: 0.5 }}
									onClick={() => navigate("/account/profile")}
								>
									<AccountIcon />
								</IconButton>
							)}
						</Grid>
						<Menu
							anchorEl={anchorEl}
							id="account-menu"
							open={isMenuOpen}
							onClose={handleClose}
							onClick={handleClose}
							disablePortal
							PaperProps={{
								elevation: 0,
								sx: {
									overflow: "visible",
									filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
									mt: 1.5,
									"& .MuiAvatar-root": {
										width: 32,
										height: 32,
										ml: -0.5,
										mr: 1,
									},
									"&:before": {
										content: '""',
										display: "block",
										position: "absolute",
										top: 0,
										right: 14,
										width: 10,
										height: 10,
										bgcolor: "background.paper",
										transform: "translateY(-50%) rotate(45deg)",
										zIndex: 0,
									},
								},
							}}
							transformOrigin={{ horizontal: "right", vertical: "top" }}
							anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
						>
							<MenuItem
								onClick={() => {
									activeAccount?.accountType === 2 || !activeAccount?.isAccountOwner
										? navigate("/account/profile/account")
										: navigate("/account/profile");
								}}
							>
								<ListItemIcon>
									<AccountIcon />
								</ListItemIcon>
								{t("menu.account.profile")}
							</MenuItem>
							<MenuItem onClick={() => navigate("/accounts")}>
								<ListItemIcon>
									<SwitchAccountIcon />
								</ListItemIcon>
								{t("profilePage.switchAccount")}
							</MenuItem>
							<Divider />
							<MenuItem onClick={() => navigate("/logout")}>
								<ListItemIcon>
									<LogoutIcon />
								</ListItemIcon>
								{t("logout")}
							</MenuItem>
						</Menu>
					</Grid>
				</Toolbar>
			</AppBar>
		</>
	);
}
